import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

/** Decode JWT payload without validating signature (client is untrusted anyway). */
function decodeJwt(token) {
  try {
    const b64 = token.split(".")[1];
    const json = atob(b64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Build a minimal user object from a JWT so the UI stays authenticated
 *  when /auth/me hasn't returned yet (or transiently fails). */
function userFromToken(token) {
  const p = decodeJwt(token);
  if (!p || !p.sub) return null;
  // Expired? treat as no user.
  if (p.exp && p.exp * 1000 < Date.now()) return null;
  return {
    id: p.sub,
    role: p.role || "agent",
    email: "",
    name: "",
    initials: "",
    phone: "",
    is_active: true,
  };
}

export function AuthProvider({ children }) {
  // Hydrate synchronously from the token so cold boot never flashes signed-out.
  const [user, setUser] = useState(() => userFromToken(tokenStore.get()));
  const [ready, setReady] = useState(false);
  const retryTimer = useRef(null);

  const clearRetry = () => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  };

  const refresh = useCallback(async () => {
    const t = tokenStore.get();
    if (!t) {
      setUser(null);
      setReady(true);
      return;
    }
    // Keep the minimal user around while we try to hydrate the full one.
    const stub = userFromToken(t);
    if (stub && !user) setUser(stub);

    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      clearRetry();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Token really is invalid — sign the user out.
        tokenStore.clear();
        setUser(null);
        clearRetry();
      } else {
        // Network / 5xx / timeout — keep the session and retry in the background.
        clearRetry();
        retryTimer.current = setTimeout(() => {
          refresh();
        }, 8000);
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    return clearRetry;
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokenStore.set(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearRetry();
    tokenStore.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
